import React from 'react';
import { InputMask, format } from '@react-input/mask';
import type { Modify, Track, TrackingData } from '@react-input/mask';
import type { InputProps } from 'antd/lib/input';
import Input from 'antd/lib/input';

type Props = InputProps & {
  /**
   * Маска
   * Указывается в виде строкового шаблона
   * Например, я 999 яя 999 - Описывает шаблон для гос. номеров
   * где я - это символ из [а-яА-Я], 9 - [0-9].
   * (см. initialFormatChars для просмотра заранее заданных шаблонных символов)
   *
   * Для расширения или переопределения таблицы шаблонных символов используется
   * свойство formatChars.
   */
  mask: string;
  /**
   * Символ, используемый в незаполненных позициях маски
   * @default _
   */
  maskChar?: string;
  /**
   * Функция преобразования при вводе
   * Например, приведение к заглавному написанию:
   * transform={(value: string) => value.toUpperCase()}
   * @param value Введенное значение
   */
  transform?: (value: string) => string;
  /**
   * Массив преобразований при вводе
   * Если необходимо последовательное преобразование
   */
  transformers?: ((value: string) => string)[];
  /**
   * Преобразовать в заглавные
   * @default false
   */
  upperCase?: boolean;
  /**
   * Преобразовать в строчные
   * @default false
   */
  lowerCase?: boolean;
  /**
   * Показывать маску?
   * @default true
   */
  showMask?: boolean;
  /**
   * Таблица подстановочных символов
   * с соответствующими им регулярками в виде строки
   * Например, нам нужен шаблон: я 555 яяя,
   * где 5 - это число [1-5], я - буква [к-я]
   * Тогда formatChars:
   * {
   *   '5': '[1-5]',
   *   'я': '[к-я]'
   * }
   */
  formatChars?: Record<string, string>;
  /**
   * Преобразование маски в зависимости от ввода
   * @return Маска
   */
  formatMask?: (value: string) => string;
  /**
   * Преобразование значения при сохранении в модель
   * Например, значение, которое в маске представляется как А 111 АА 116
   * можно сохранить как А111АА116:
   * (value: string) => value.replace(/\s+/g, '')
   * @param value Преобразованное значение
   */
  convertOnSave?: (value: string) => string | null;
};

type State = {
  // Значение в том виде, в котором оно отображается в поле
  value: string;
  mask: string;
};

/**
 * Символы в маске
 * Все символы указывать в латинской раскладке
 */
const initialFormatChars = {
  '9': '[0-9]', // числа от 0 до 9
  l: '[A-Za-z]', // latin
  '*': '[A-Za-zА-Яа-я0-9]', // любые буквы и числа
  c: '[А-Яа-я]', // cyrillic
};

const MaskedInput = React.forwardRef<HTMLInputElement, any>(
  (props, forwardedRef) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const forwardedRefRef = React.useRef(forwardedRef);
  forwardedRefRef.current = forwardedRef;

  const forwardElement = (element: HTMLInputElement | null) => {
    const ref = forwardedRefRef.current;
    if (typeof ref === 'function') ref(element);
    else if (ref) ref.current = element;
  };

  /**
   * antd пересоздает ref-объект поля на каждом рендере, и React вызывает ref
   * с null и снова с полем. InputMask на это перерегистрирует поле и теряет
   * позицию курсора, поэтому дальше передается только смена DOM-элемента
   */
  const setInputRef = React.useCallback((input: any) => {
    const element = input && input.input ? input.input : input;
    if (!element || element === inputRef.current) return;
    inputRef.current = element;
    forwardElement(element);
  }, []);

  React.useLayoutEffect(() => {
    // Повторная привязка после размонтирования эффектов в StrictMode
    if (inputRef.current) forwardElement(inputRef.current);
    return () => {
      if (inputRef.current) inputRef.current.blur();
      forwardElement(null);
    };
  }, []);

  return <Input {...props} ref={setInputRef} />;
  }
);

/**
 * Символ незаполненной позиции.
 * InputMask сам по себе принимает в позиции любой символ,
 * проверка по formatChars выполняется в track
 */
const REPLACEMENT_CHAR = '_';
const replacement = { [REPLACEMENT_CHAR]: /./ };

const toRegExp = (pattern: string) => new RegExp(`^(?:${pattern})$`);

export default class MaskInput extends React.Component<Props, State> {
  static defaultProps = {
    maskChar: '_',
    formatChars: {},
    showMask: true,
    disabled: false,
  };

  // Последнее значение, отправленное в onChange
  emittedValue: unknown = undefined;
  // Маска, выбранная в modify для текущего ввода
  nextMask: string | null = null;

  constructor(props: Props) {
    super(props);
    this.state = this.getStateFromValue(props.value);
  }

  getFormat = (): Record<string, string> =>
    Object.assign({}, initialFormatChars, this.props.formatChars);

  isToken = (character: string) =>
    Object.prototype.hasOwnProperty.call(this.getFormat(), character);

  /**
   * Маска в формате InputMask: шаблонные символы заменяются на REPLACEMENT_CHAR
   */
  toReplacementMask = (mask: string) =>
    mask
      .split('')
      .map((character) => (this.isToken(character) ? REPLACEMENT_CHAR : character))
      .join('');

  /**
   * Маска для значения: пока значение пустое, используется маска из пропсов
   */
  getMask = (userChars: string) => {
    const { formatMask, mask } = this.props;
    if (!userChars || !formatMask) return mask;
    return formatMask(userChars) || mask;
  };

  /**
   * Символы, введенные пользователем, из части отображаемого значения
   * @param value Отображаемое значение (или его часть)
   * @param mask Маска, по которой отображено значение
   * @param offset Позиция части в отображаемом значении
   */
  getUserChars = (value: string, mask: string, offset = 0) =>
    value
      .split('')
      .filter(
        (character, index) =>
          this.isToken(mask[offset + index]) && character !== REPLACEMENT_CHAR
      )
      .join('');

  /**
   * Разбор произвольного значения (с разделителями маски или без)
   * в символы, введенные пользователем
   */
  parseUserChars = (value: string, mask: string) => {
    const format = this.getFormat();
    let result = '';
    let maskIndex = 0;
    for (const character of value) {
      if (character === REPLACEMENT_CHAR) continue;
      // Пропускаем разделители маски, которых нет в значении
      while (maskIndex < mask.length && !this.isToken(mask[maskIndex])) {
        if (mask[maskIndex] === character) break;
        maskIndex++;
      }
      if (maskIndex >= mask.length) break;
      if (!this.isToken(mask[maskIndex])) {
        maskIndex++;
      } else if (toRegExp(format[mask[maskIndex]]).test(character)) {
        result += character;
        maskIndex++;
      }
    }
    return result;
  };

  toDisplayValue = (userChars: string, mask: string) => {
    if (!userChars) return '';
    const replacementMask = this.toReplacementMask(mask);
    const formatted = format(userChars, { mask: replacementMask, replacement });
    return this.props.showMask
      ? formatted + replacementMask.slice(formatted.length)
      : formatted;
  };

  getStateFromValue = (value: unknown): State => {
    const stringValue = value == null ? '' : String(value);
    const mask = this.getMask(stringValue);
    return {
      mask,
      value: this.toDisplayValue(this.parseUserChars(stringValue, mask), mask),
    };
  };

  transformInput = (data: string) => {
    const { transform, transformers, upperCase, lowerCase } = this.props;
    let value = data;
    if (transformers) {
      transformers.forEach((transform) => {
        value = transform(value);
      });
    } else if (transform) {
      value = transform(value);
    }
    if (upperCase) {
      value = value.toUpperCase();
    } else if (lowerCase) {
      value = value.toLowerCase();
    }
    return value;
  };

  /**
   * Символы, введенные пользователем, до и после места изменения
   */
  getChangeContext = ({ value, selectionStart, selectionEnd }: TrackingData) => {
    const { mask } = this.state;
    return {
      before: this.getUserChars(value.slice(0, selectionStart), mask),
      after: this.getUserChars(value.slice(selectionEnd), mask, selectionEnd),
    };
  };

  /**
   * Оставляет из вводимых символов только те, что подходят под маску
   * в позиции ввода (маска пересчитывается по мере ввода)
   */
  filterInput = (data: string, before: string, after: string) => {
    const format = this.getFormat();
    const isPaste = data.length > 1;
    let accepted = '';
    let literalsMatched = 0;
    for (const character of data) {
      const index = before.length + accepted.length;
      const mask = this.getMask(before + accepted + character + after);
      const tokenPositions = mask
        .split('')
        .map((maskCharacter, position) => (this.isToken(maskCharacter) ? position : -1))
        .filter((position) => position !== -1);
      if (index >= tokenPositions.length) break;
      // При вставке пропускаем разделители маски, например +7 ( в телефоне
      const literals = mask.slice(
        index === 0 ? 0 : tokenPositions[index - 1] + 1,
        tokenPositions[index]
      );
      if (isPaste && character === literals[literalsMatched]) {
        literalsMatched++;
        continue;
      }
      const pattern = format[mask[tokenPositions[index]]];
      if (toRegExp(pattern).test(character)) {
        accepted += character;
        literalsMatched = 0;
      }
    }
    return accepted;
  };

  track: Track = (trackingData) => {
    if (trackingData.inputType !== 'insert') return undefined;
    const { before, after } = this.getChangeContext(trackingData);
    const accepted = this.filterInput(
      this.transformInput(trackingData.data),
      before,
      after
    );
    // Ни один символ не подошел: ввод игнорируется
    return accepted || false;
  };

  modify: Modify = (trackingData) => {
    const { before, after } = this.getChangeContext(trackingData);
    const data =
      trackingData.inputType === 'insert'
        ? this.filterInput(this.transformInput(trackingData.data), before, after)
        : '';
    this.nextMask = this.getMask(before + data + after);
    return { mask: this.toReplacementMask(this.nextMask) };
  };

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value, name },
    } = event;
    const { onChange, convertOnSave } = this.props;
    const mask = this.nextMask || this.state.mask;
    this.nextMask = null;
    /**
     * Удаляем символ незаполненной позиции из value
     * чтобы в значение модели не попадали символы маски
     */
    let clearedValue: string | null = value.split(REPLACEMENT_CHAR).join('');
    if (convertOnSave) {
      clearedValue = convertOnSave(clearedValue || '');
    }
    this.emittedValue = clearedValue;
    this.setState({ value, mask });
    if (onChange)
      onChange({
        ...event,
        target: { ...event.target, value: clearedValue, name },
      } as any);
  };

  componentDidUpdate(prevProps: Props) {
    const { mask, value } = this.props;
    // Значение поменяли снаружи (не через ввод в поле)
    if (prevProps.value !== value && value !== this.emittedValue) {
      this.emittedValue = undefined;
      this.setState(this.getStateFromValue(value));
    } else if (prevProps.mask !== mask) {
      this.setState(this.getStateFromValue(value));
    }
  }

  render() {
    const {
      formatChars,
      maskChar,
      showMask,
      upperCase,
      lowerCase,
      convertOnSave,
      formatMask,
      transform,
      transformers,
      mask: _mask,
      value: _value,
      ...props
    } = this.props;
    return (
      <InputMask
        {...props}
        type={props.type || 'text'}
        value={this.state.value}
        component={MaskedInput}
        mask={this.toReplacementMask(this.state.mask)}
        replacement={replacement}
        showMask={showMask}
        track={this.track}
        modify={this.modify}
        onChange={this.handleChange}
        autoComplete="off"
      />
    );
  }
}
